import { Image as ImageIcon, Video as VideoIcon, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCharacters, type CharacterCard } from "@/hooks";
import { Skeleton } from "@/components/ui";
import { AssetEmptyState } from "@/components/features/assets";

interface CharacterGridProps {
  /** Renders the empty state's primary CTA — used to send users to T2I. */
  onCreateClick: () => void;
  /**
   * Override what happens when a card is clicked. Defaults to
   * navigating to the character detail page; the Edit Character flow
   * passes a custom handler to stay inside `/characters/edit` instead.
   */
  onSelect?: (name: string) => void;
}

/**
 * Top-level Characters tab content. One card per character, click-through
 * to detail by default (or to whatever `onSelect` does). Empty state
 * nudges the user to T2I as the entry point.
 */
export function CharacterGrid({ onCreateClick, onSelect }: CharacterGridProps) {
  const navigate = useNavigate();
  const { data, isLoading } = useCharacters();
  const characters = data?.characters ?? [];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />
        ))}
      </div>
    );
  }

  if (characters.length === 0) {
    // Reuse the standard empty-state component so the Characters tab
    // matches the visual treatment of the regular Assets empty state.
    return (
      <AssetEmptyState
        filter="characters"
        onNavigate={() => onCreateClick()}
        fullPage
      />
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {characters.map((c) => (
        <CharacterGridCard
          key={c.name}
          character={c}
          onClick={() =>
            onSelect
              ? onSelect(c.name)
              : navigate(`/characters/${encodeURIComponent(c.name)}`)
          }
        />
      ))}
    </div>
  );
}

interface CharacterGridCardProps {
  character: CharacterCard;
  onClick: () => void;
}

function CharacterGridCard({ character, onClick }: CharacterGridCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative rounded-2xl overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.15] transition-all text-left"
    >
      <div className="relative aspect-[3/4] bg-black">
        {character.canonical_url ? (
          <img
            src={character.canonical_url}
            alt={character.name}
            loading="lazy"
            className="absolute inset-0 size-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#444]">
            <Users className="size-8" />
          </div>
        )}
        {/* Bottom gradient + name */}
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
          <div className="text-sm font-medium text-white truncate">
            {character.name}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-white/70">
            <span className="flex items-center gap-1">
              <ImageIcon className="size-3" />
              {character.image_count}
            </span>
            <span className="flex items-center gap-1">
              <VideoIcon className="size-3" />
              {character.video_count}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
